import React, { useEffect, useState, useContext } from 'react';
import axios from 'axios';
import { useParams, useNavigate } from 'react-router-dom';
import BoardCodeForm from '../../components/Code/BoardCodeForm';
import OrgSelect from '../../components/OrgSelect/OrgSelect';
import { AuthContext } from '../../context/AuthContext';

function BoardCodeEditPage() {
  const { id } = useParams();
  const [code, setCode] = useState(null);
  const navigate = useNavigate();
  const { token } = useContext(AuthContext);

  // 권한 관리 상태
  const [boardAuths, setBoardAuths] = useState([]);
  const [showOrgSelect, setShowOrgSelect] = useState(false);
  const [selectedAuthType, setSelectedAuthType] = useState('read');

  useEffect(() => {
    const fetchCode = async () => {
      try {
        const res = await axios.get(`/api/board-codes/${id}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setCode(res.data);
      } catch (err) {
        console.error('게시판 코드 조회 실패:', err);
        alert('코드를 불러오지 못했습니다');
      }
    };

    const fetchAuths = async () => {
      try {
        const res = await axios.get(`/api/board-codes/auths/board-code-id/${id}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        // API 응답이 배열인지 확인하고, 아니면 빈 배열로 설정
        setBoardAuths(Array.isArray(res.data) ? res.data : []);
      } catch (err) {
        console.error('게시판 권한 조회 실패:', err);
        // 권한이 없을 경우 빈 배열로 설정
        setBoardAuths([]);
      }
    };

    fetchCode();
    fetchAuths();
  }, [id, token]);

  const handleSubmit = async (formData) => {
    try {
      // 게시판 코드 수정
      await axios.put(`/api/board-codes/${id}`, formData, {
        headers: { Authorization: `Bearer ${token}` },
      });

      // 수정된 게시판 코드 정보 다시 조회
      const updatedCodeResponse = await axios.get(`/api/board-codes/${id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const actualBoardCode = updatedCodeResponse.data.code;

      // 기존 권한 전체 삭제 후 새 권한 일괄 생성
      await axios.delete(`/api/board-codes/auths/board/${actualBoardCode}`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (Array.isArray(boardAuths) && boardAuths.length > 0) {
        const authsWithBoardCode = boardAuths.map(auth => ({
          ...auth,
          board_code: actualBoardCode
        }));

        await axios.post('/api/board-codes/auths/bulk', authsWithBoardCode, {
          headers: { Authorization: `Bearer ${token}` },
        });
      }

      alert('게시판 코드가 수정되었습니다');
      navigate(`/board-codes/${id}`);
    } catch (err) {
      console.error('게시판 코드 수정 실패:', err);
      alert(err.response?.data?.message || '수정 중 오류 발생');
    }
  };

  // 권한 관리 함수들
  const handleAddAuth = (authType) => {
    setSelectedAuthType(authType);
    setShowOrgSelect(true);
  };

  const handleOrgSelectConfirm = (selectedTargets) => {
    const newAuths = selectedTargets.map(target => ({
      target_type: target.target_type,
      target_id: target.target_id,
      target_name: target.target_name,
      auth_type: selectedAuthType
    }));

    // 중복 권한 제거
    const filteredAuths = newAuths.filter(newAuth =>
      !boardAuths.some(existing =>
        existing.target_type === newAuth.target_type &&
        existing.target_id === newAuth.target_id &&
        existing.auth_type === newAuth.auth_type
      )
    );

    setBoardAuths(prev => [...prev, ...filteredAuths]);
    setShowOrgSelect(false);
  };

  const handleOrgSelectCancel = () => {
    setShowOrgSelect(false);
  };

  const handleRemoveAuth = (index) => {
    setBoardAuths(prev => prev.filter((_, i) => i !== index));
  };

  const getAuthTypeLabel = (authType) => {
    const labels = { 'read': '읽기', 'write': '쓰기', 'manage': '관리' };
    return labels[authType] || authType;
  };

  const getTargetTypeLabel = (targetType) => {
    return targetType === 'user' ? '사용자' : '부서';
  };

  if (!code) return <div className="loading">Loading...</div>;

  return (
    <div className="view-boardcode container-fluid">
      <div className="row">
        <div className="col-12">

          {/* 헤더 영역 (상단 고정) */}
          <div className="boardcode-header-fixed d-flex justify-content-end gap-2">
            <button
              type="submit"
              form="board-code-form"   // 폼 외부에서도 제출 가능
              className="btn btn-primary"
            >
              저장
            </button>
            <button
              className="btn btn-outline-secondary"
              onClick={() => navigate('/board-codes')}
            >
              목록으로
            </button>
          </div>

          {/* 수정 폼 박스 */}
          <div className="boardcode-body boardcode-basic">
            <h6>✏️ 게시판 코드 수정</h6>
            <BoardCodeForm
              initialData={code}
              onSubmit={handleSubmit}
              formId="board-code-form"  // 폼에 id 부여
            />
          </div>

          {/* 권한 관리 박스 */}
          <div className="boardcode-body boardcode-basic mt-4">
            <h6>🔐 게시판 권한 설정</h6>

            {/* 권한 추가 버튼들 */}
            <div className="mb-3">
              <label className="form-label">권한 추가:</label>
              <div className="d-flex gap-2">
                <button
                  type="button"
                  className="btn btn-outline-success btn-sm"
                  onClick={() => handleAddAuth('read')}
                >
                  읽기 권한 추가
                </button>
                <button
                  type="button"
                  className="btn btn-outline-warning btn-sm"
                  onClick={() => handleAddAuth('write')}
                >
                  쓰기 권한 추가
                </button>
                <button
                  type="button"
                  className="btn btn-outline-danger btn-sm"
                  onClick={() => handleAddAuth('manage')}
                >
                  관리 권한 추가
                </button>
              </div>
            </div>

            {/* 권한 목록 */}
            {Array.isArray(boardAuths) && boardAuths.length > 0 && (
              <div className="mb-3">
                <label className="form-label">설정된 권한:</label>
                <div className="table-responsive">
                  <table className="table table-sm table-bordered">
                    <thead className="table-light">
                      <tr>
                        <th>대상 유형</th>
                        <th>대상 ID</th>
                        <th>대상 이름</th>
                        <th>권한 유형</th>
                        <th>관리</th>
                      </tr>
                    </thead>
                    <tbody>
                      {boardAuths.map((auth, index) => (
                        <tr key={index}>
                          <td>
                            <span className={`badge ${auth.target_type === 'user' ? 'bg-primary' : 'bg-info'}`}>
                              {getTargetTypeLabel(auth.target_type)}
                            </span>
                          </td>
                          <td>{auth.target_id}</td>
                          <td>{auth.target_name || '-'}</td>
                          <td>
                            <span className={`badge ${
                              auth.auth_type === 'manage' ? 'bg-danger' :
                              auth.auth_type === 'write' ? 'bg-warning' : 'bg-success'
                            }`}>
                              {getAuthTypeLabel(auth.auth_type)}
                            </span>
                          </td>
                          <td>
                            <button
                              type="button"
                              className="btn btn-outline-danger btn-sm"
                              onClick={() => handleRemoveAuth(index)}
                            >
                              삭제
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>

          {/* OrgSelect 팝업 */}
          {showOrgSelect && (
            <OrgSelect
              onConfirm={handleOrgSelectConfirm}
              onCancel={handleOrgSelectCancel}
              selectionMode="multiple"
              targetType="all"
              initialSelected={boardAuths
                .filter(auth => auth.auth_type === selectedAuthType)
                .map(auth => ({
                  target_type: auth.target_type,
                  target_id: auth.target_id,
                  target_name: auth.target_name,
                  disp_target_name: auth.target_name
                }))}
            />
          )}

        </div>
      </div>
    </div>
  );


}

export default BoardCodeEditPage;